import {resetAllStores} from "@/store/createStore";
import {acceptLanguageAtom} from "@/store/jotaiAtoms";
import {useTokenStore} from "@/store/token";
import type {TToken} from "@/store/types";
import axios from "axios";
import createAuthRefreshInterceptor, {type AxiosAuthRefreshRequestConfig} from "axios-auth-refresh";
import {getDefaultStore} from "jotai";

const setTokens = (tokens: TToken) => {
  useTokenStore.getState().setAccessToken(tokens.access);
  useTokenStore.getState().setRefreshToken(tokens.refresh);
};

const baseUrl = import.meta.env.VITE_APP_API_BASE_URL ? `${import.meta.env.VITE_APP_API_BASE_URL}/api/` : "/api/";

const AxiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

AxiosInstance.interceptors.request.use(request => {
  const accessToken = useTokenStore.getState().access;
  const lang = getDefaultStore().get(acceptLanguageAtom);

  if (request.headers) {
    request.headers["Accept-Language"] = lang;

    if (accessToken) {
      request.headers["Authorization"] = `Bearer ${accessToken}`;
    }
  } else {
    Object.assign(request, {
      headers: {
        "Accept-Language": lang,
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    });
  }

  return request;
});
createAuthRefreshInterceptor(AxiosInstance, async () => {
  try {
    const response = await axios.post<TToken>(
      baseUrl + "token/refresh/",
      {
        refresh: useTokenStore.getState().refresh,
      },
      {
        skipAuthRefresh: true,
      } as AxiosAuthRefreshRequestConfig,
    );
    if (response.status === 401) {
      await useTokenStore.getState().logout();

      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = `${window.location.origin}/login/`;
      }
    }
    setTokens(response.data);
  } catch (error) {
    const isLoggedIn = !!useTokenStore.getState().userId;

    if (isLoggedIn) {
      resetAllStores();
    }
  }
});

const client = AxiosInstance;

export {client};
